import React from "react";
import { Menu, Typography, Button, ListSubheader } from "@mui/material";

import { UserData } from "../../context/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faKey, faSignOut, faUser } from "@fortawesome/free-solid-svg-icons";
import { defineMessage, FormattedMessage } from "react-intl";
import CRUDFormDialog from "../CRUDFormDialog";
import { useNotification } from "../NotificationProvider";
import { Item, translations } from "./shared";
import Applications from "./Applications";

type UserInfoProps = {
    userInfo: UserData;
};

function _getAccountInfoMenuItem(userInfo: UserData) {
    return <Item href={userInfo.accountPage} icon={faUser} message={translations.account_info} />;
}

const changePasswordMessage = defineMessage({
    id: "change_password.create",
    defaultMessage: "Change password",
});

const PasswordChangeMenuItem: React.FC = () => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const { showSuccess } = useNotification();

    return (
        <>
            <Item
                href="/changepassword"
                onClick={() => setDialogOpen(true)}
                icon={faKey}
                message={changePasswordMessage}
            />
            <CRUDFormDialog
                open={dialogOpen}
                title={changePasswordMessage}
                hide={() => setDialogOpen(false)}
                resourceName="change_password"
                action="create"
                onSuccess={() => {
                    showSuccess(
                        <FormattedMessage
                            id="change_password_success"
                            defaultMessage={"Password successfully changed"}
                        />,
                        <FormattedMessage id="success" defaultMessage="Success" />,
                    );
                }}
            />
        </>
    );
};

const UserInfo = ({ userInfo }: UserInfoProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const logout = async (): Promise<void> => {
        // Ends the session and revokes every token issued to it, this UI's own included.
        try {
            await fetch("/openid-provider/end-session", { method: "POST", credentials: "same-origin" });
        } catch (e) {
            console.error("Logging out has failed", e);
        }
        window.location.href = window.location.origin;
    };
    const hasPasswordChange = global.identityProvider === "internal" && userInfo.userId;
    const hasAccountInfo = !hasPasswordChange && userInfo.userId;

    return (
        <div>
            <Button onClick={handleClick} sx={{ textTransform: "none" }}>
                <Typography variant="subtitle1" style={{ flexGrow: 1 }}>
                    {userInfo.name}
                </Typography>
                <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 5 }} />
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                <ListSubheader>
                    <FormattedMessage id="account.account" defaultMessage="Account" />
                </ListSubheader>
                {hasPasswordChange ? <PasswordChangeMenuItem /> : null}
                {hasAccountInfo ? _getAccountInfoMenuItem(userInfo) : null}
                <Item
                    href="/logout"
                    onClick={logout}
                    icon={faSignOut}
                    message={{ id: "logout", defaultMessage: "Logout" }}
                />
                <Applications opened={open} />
            </Menu>
        </div>
    );
};

export default UserInfo;
